import { BaseAPIURL } from "./base";

// ---------------------------------------------------------------------
// Guest checkout - booking without an account.
//
// Contract: API_CHANGES_NEEDED.md § E. None of these endpoints exist on
// the backend yet; until they do, calls fail and the UI shows an error
// toast rather than pretending the booking went through.
//
// Flow:
//   1. send-code    { email }            -> 6-digit code emailed
//   2. verify-code  { email, code }      -> guestVerificationToken (24h)
//   3. booking/guest { ...trip, guest*, guestVerificationToken }
//   4. booking/lookup { bookingId, email } -> read-only booking
//
// The token is remembered in localStorage (one email at a time) so a guest
// booking again with the same email on this device within 24 hours isn't
// asked for another code.
// ---------------------------------------------------------------------

export const guestSendCodeUrl = `${BaseAPIURL}booking/guest/send-code`;
export const guestVerifyCodeUrl = `${BaseAPIURL}booking/guest/verify-code`;
export const guestBookingUrl = `${BaseAPIURL}booking/guest`;
export const bookingLookupUrl = `${BaseAPIURL}booking/lookup`;

const TOKEN_KEY = "wat_guest_verification";

export const normalizeEmail = (email) => (email || "").trim().toLowerCase();

// Returns the stored token only if it's for this email and hasn't expired.
export const getGuestToken = (email) => {
  try {
    const stored = JSON.parse(localStorage.getItem(TOKEN_KEY));
    if (!stored?.token || stored.email !== normalizeEmail(email)) return null;
    if (new Date(stored.expiresAt).getTime() <= Date.now()) {
      localStorage.removeItem(TOKEN_KEY);
      return null;
    }
    return stored.token;
  } catch {
    return null;
  }
};

export const storeGuestToken = (email, token, expiresAt) => {
  try {
    localStorage.setItem(
      TOKEN_KEY,
      JSON.stringify({
        email: normalizeEmail(email),
        token,
        // Fall back to 24h if the backend leaves expiresAt out
        expiresAt: expiresAt || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
      })
    );
  } catch {
    // storage unavailable (private browsing, etc.) - the guest just gets
    // asked for a code again next time
  }
};

export const clearGuestToken = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // ignore
  }
};

// All four calls resolve to { ok, status, data } instead of throwing on a
// non-2xx, so callers can branch on 401/403/404/429 directly.
const postJson = async (url, body) => {
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  let data = null;
  try {
    data = await response.json();
  } catch {
    // empty or non-JSON body
  }
  return { ok: response.ok, status: response.status, data };
};

export const sendGuestCode = (email) =>
  postJson(guestSendCodeUrl, { email: email.trim() });

export const verifyGuestCode = (email, code) =>
  postJson(guestVerifyCodeUrl, { email: email.trim(), code });

export const createGuestBooking = (payload) => postJson(guestBookingUrl, payload);

export const lookupBooking = (bookingId, email) =>
  postJson(bookingLookupUrl, { bookingId: bookingId.trim(), email: email.trim() });

// The contact fields a guest booking stores, mapped onto Signup.js's field
// names - used to prefill "Create an account" from a guest booking.
export const signupPrefillFromBooking = (booking) => ({
  firstName: booking?.guestFirstName || "",
  lastName: booking?.guestLastName || "",
  email: booking?.guestEmail || "",
  mobileNumber: booking?.guestMobileNumber || "",
  countryCode: booking?.guestCountryCode || "",
  country: booking?.guestCountry || "",
  isWhatsAppUser: !!(booking?.isWhatsAppUser ?? booking?.guestIsWhatsAppUser),
});
